/**
 * Oversoles — per-breakpoint product gallery type.
 *
 * The Digital theme's `main-product` section exposes a single `gallery_type`
 * setting (slider_previews / slider_bullets / grid_gallery / stacked_gallery)
 * that drives both the DOM (swiper wrapper for sliders) and the layout CSS
 * class on `.product__outer`.
 *
 * We've split that single setting into `gallery_type_desktop` and
 * `gallery_type_mobile`. The Liquid template now always renders the swiper
 * wrapper (so a slider can mount on either breakpoint) and emits the desktop
 * and mobile values as data attributes on the `.product` element. This script
 * picks the active type on load + on viewport change and:
 *
 *   1. Swaps the `product--{type}` and `product__outer--{type}` modifier
 *      classes so the existing Digital CSS rules apply correctly.
 *   2. Initializes Swiper (via the global `sliderInit`) when the active type
 *      is a slider, and destroys the Swiper instance when it isn't, freeing
 *      the layout for grid / stacked CSS rules.
 *
 * The breakpoint boundary is 1100px to match the Digital theme's
 * `.product__outer` desktop layout media query.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var DESKTOP_QUERY = '(min-width: 1100px)';
  var TYPE_TO_CLASS = {
    slider_previews: 'slider-previews',
    slider_bullets: 'slider-bullets',
    grid_gallery: 'grid-gallery',
    stacked_gallery: 'stacked-gallery'
  };
  var ALL_CLASSES = ['slider-previews', 'slider-bullets', 'grid-gallery', 'stacked-gallery'];

  function isSliderType(type) {
    return type === 'slider_previews' || type === 'slider_bullets';
  }

  function activeType(productEl) {
    var mq = window.matchMedia(DESKTOP_QUERY);
    return mq.matches
      ? productEl.dataset.galleryDesktop
      : productEl.dataset.galleryMobile;
  }

  function applyClass(productEl) {
    var active = activeType(productEl);
    if (!active || !TYPE_TO_CLASS[active]) return;
    var modifier = TYPE_TO_CLASS[active];
    var outer = productEl.querySelector('.product__outer');

    ALL_CLASSES.forEach(function (cls) {
      productEl.classList.remove('product--' + cls);
      if (outer) outer.classList.remove('product__outer--' + cls);
    });
    productEl.classList.add('product--' + modifier);
    if (outer) outer.classList.add('product__outer--' + modifier);
  }

  function applySwiperState(productEl) {
    var active = activeType(productEl);
    if (!active) return;
    var mediaList = productEl.querySelector('.js-media-list');
    if (!mediaList) return;
    var swiperInstance = mediaList.swiper;

    if (isSliderType(active)) {
      // Active mode wants a slider. Re-init via the theme's global helper if
      // the Swiper instance is missing or was previously destroyed.
      var needsInit = !swiperInstance || swiperInstance.destroyed;
      if (needsInit && typeof sliderInit === 'function') {
        try {
          sliderInit(true);
        } catch (e) {
          /* swallow — the theme's global will retry on next event */
        }
      }
    } else {
      // Active mode wants grid / stacked. Tear down Swiper so it stops
      // applying inline transforms / widths to slides.
      if (swiperInstance && !swiperInstance.destroyed) {
        try {
          swiperInstance.destroy(true, true);
        } catch (e) {
          /* swallow */
        }
      }
    }
  }

  function applyAll() {
    var products = document.querySelectorAll(
      '.product[data-gallery-desktop][data-gallery-mobile]'
    );
    products.forEach(function (productEl) {
      applyClass(productEl);
      applySwiperState(productEl);
    });
  }

  /**
   * The theme's variant-change handler in global.js calls `sliderInit(true)`
   * after replacing `.product__media-list` innerHTML, which re-mounts a
   * Swiper instance even when the active mode is grid / stacked. Watch the
   * `.js-media-list` class attribute and re-evaluate every time Swiper adds
   * its `swiper-initialized` flag so we tear it back down if needed.
   */
  function attachObservers() {
    var mediaLists = document.querySelectorAll(
      '.product[data-gallery-desktop][data-gallery-mobile] .js-media-list'
    );
    mediaLists.forEach(function (mediaList) {
      if (mediaList.__oversolesGalleryObs) return;
      var obs = new MutationObserver(function () {
        var productEl = mediaList.closest('.product');
        if (!productEl) return;
        var active = activeType(productEl);
        if (!isSliderType(active) && mediaList.swiper && !mediaList.swiper.destroyed) {
          try {
            mediaList.swiper.destroy(true, true);
          } catch (e) {
            /* swallow */
          }
        }
      });
      obs.observe(mediaList, { attributes: true, attributeFilter: ['class'] });
      mediaList.__oversolesGalleryObs = obs;
    });
  }

  function init() {
    applyAll();
    attachObservers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Run again after `load` so the deferred sliderInit has had a chance to
  // mount Swiper before we decide whether to keep it.
  window.addEventListener('load', init);

  // React to viewport crossings.
  var mq = window.matchMedia(DESKTOP_QUERY);
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', applyAll);
  } else if (typeof mq.addListener === 'function') {
    mq.addListener(applyAll);
  }

  // Theme editor / quick-add: section is reloaded.
  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 50);
  });
})();
